import { useEffect, useMemo, useRef, useState } from 'react';
import { Vibration } from 'react-native';
import * as Speech from 'expo-speech';

import { useEventCallback } from '../../../common/utils/hooks';
import { buildSuggestedTubBreakdown, formatKgFromGrams, resolveTubWeightConfig } from '../../../common/utils/pickList/tubWeight';

import { getPickedQuantity, toNonNegativeNumber } from './PickListDetailHelpers';

const TUB_SWITCH_VIBRATION_PATTERN = [0, 250, 120, 250];

// Tub weight totals for the docked tub card, plus the "Change to tub N" prompt
// (vibration and speech) the picker acknowledges on the device.
const usePickListTubWeight = ({ mappedPickInventory, tenantSettings }) => {
	const [pendingTubSwitchIndex, setPendingTubSwitchIndex] = useState(null);
	// State mirror of highestAcknowledgedTubIndexRef so the docked tub card can
	// show the acknowledged tub before the calculated breakdown reaches it. The
	// picked-total snapshot attributes weight picked after acknowledgement to the
	// new tub while the estimate still packs everything into earlier tubs.
	const [acknowledgedTub, setAcknowledgedTub] = useState(null);
	const highestPromptedTubIndexRef = useRef(null);
	const highestAcknowledgedTubIndexRef = useRef(null);

	const tubWeightConfig = useMemo(() => resolveTubWeightConfig(tenantSettings), [tenantSettings]);
	const tubWeightSummary = useMemo(() => {
		const groupedWeightTotals = mappedPickInventory.reduce((acc, item) => {
			const requiredQuantity = toNonNegativeNumber(item?.requiredQuantity);
			const pickedQuantity = getPickedQuantity(item);
			const productId = item?.product?.id;
			const excludeFromTubWeightCalculation = Boolean(item?.product?.excludeFromTubWeightCalculation);
			const excludedQuantity = Math.min(toNonNegativeNumber(item?.tubWeightExcludedQuantity), pickedQuantity);
			const effectiveRequiredQuantity = Math.max(0, requiredQuantity - excludedQuantity);
			const effectivePickedQuantity = Math.max(0, pickedQuantity - excludedQuantity);

			// Skip lines that aren't part of the pick.
			if (!requiredQuantity || !productId) {
				return acc;
			}
			// Product is explicitly excluded from tub weight calculations.
			if (excludeFromTubWeightCalculation) {
				return acc;
			}
			if (!effectiveRequiredQuantity && !effectivePickedQuantity) {
				return acc;
			}

			const unitWeightGrams = Number(item.product.weight);
			if (!Number.isFinite(unitWeightGrams)) {
				acc.missingWeightLineCount += 1;
				acc.missingWeightRequiredQuantity += effectiveRequiredQuantity;
				acc.missingWeightPickedQuantity += effectivePickedQuantity;
				return acc;
			}

			const requiredProductGrams = effectiveRequiredQuantity * unitWeightGrams;
			const pickedProductGrams = effectivePickedQuantity * unitWeightGrams;

			acc.requiredProductGrams += requiredProductGrams;
			acc.pickedProductGrams += pickedProductGrams;
			acc.excludedPickedQuantity += excludedQuantity;
			acc.requiredProductGramsByProductId[productId] = (acc.requiredProductGramsByProductId[productId] || 0) + requiredProductGrams;
			acc.pickedProductGramsByProductId[productId] = (acc.pickedProductGramsByProductId[productId] || 0) + pickedProductGrams;
			return acc;
		}, {
			requiredProductGrams: 0,
			pickedProductGrams: 0,
			excludedPickedQuantity: 0,
			missingWeightLineCount: 0,
			missingWeightRequiredQuantity: 0,
			missingWeightPickedQuantity: 0,
			requiredProductGramsByProductId: {},
			pickedProductGramsByProductId: {},
		});

		const requiredProductGroups = Object.keys(groupedWeightTotals.requiredProductGramsByProductId).map(productId => ({
			productId,
			productGrams: groupedWeightTotals.requiredProductGramsByProductId[productId],
		}));
		const pickedProductGroups = Object.keys(groupedWeightTotals.pickedProductGramsByProductId).map(productId => ({
			productId,
			productGrams: groupedWeightTotals.pickedProductGramsByProductId[productId],
		}));

		const totals = {
			requiredProductGrams: groupedWeightTotals.requiredProductGrams,
			pickedProductGrams: groupedWeightTotals.pickedProductGrams,
			excludedPickedQuantity: groupedWeightTotals.excludedPickedQuantity,
			missingWeightLineCount: groupedWeightTotals.missingWeightLineCount,
			missingWeightRequiredQuantity: groupedWeightTotals.missingWeightRequiredQuantity,
			missingWeightPickedQuantity: groupedWeightTotals.missingWeightPickedQuantity,
		};

		const requiredBreakdown = buildSuggestedTubBreakdown(requiredProductGroups, tubWeightConfig);
		const pickedBreakdown = buildSuggestedTubBreakdown(pickedProductGroups, tubWeightConfig);

		return {
			config: requiredBreakdown.config,
			totals,
			breakdown: {
				required: requiredBreakdown,
				picked: pickedBreakdown,
			},
		};
	}, [mappedPickInventory, tubWeightConfig]);

	const tubWeightDisplay = useMemo(() => {
		const config = tubWeightSummary?.config;
		const pickedBreakdown = tubWeightSummary?.breakdown?.picked;
		const requiredBreakdown = tubWeightSummary?.breakdown?.required;

		if (!config || !pickedBreakdown || !requiredBreakdown) {
			return null;
		}

		const pickedTubes = pickedBreakdown.tubes || [];
		const currentTub = pickedTubes.length ? pickedTubes[pickedTubes.length - 1] : {
			index: 1,
			productGrams: 0,
			tareGrams: config.tubTareWeightGrams || 0,
			totalGrams: config.tubTareWeightGrams || 0,
		};
		const requiredTubeForCurrent = requiredBreakdown?.tubes?.[currentTub.index - 1];
		const fallbackSuggestedProductGrams = requiredBreakdown?.averageProductGramsPerTub || config.maxTubProductWeightGrams || 0;
		const suggestedTubProductGrams = requiredTubeForCurrent?.productGrams || fallbackSuggestedProductGrams;

		const pickedTotalProductKg = formatKgFromGrams(pickedBreakdown.totalProductGrams);
		const requiredTotalProductKg = formatKgFromGrams(requiredBreakdown.totalProductGrams);
		const needsNextTubPrompt = Boolean(requiredBreakdown.tubeCount > currentTub.index &&
			currentTub.productGrams >= suggestedTubProductGrams);

		// Once the picker acknowledges a tub change, the docked card follows that
		// acknowledgement: weight picked since then counts toward the acknowledged
		// tub, even while the recalculated packing estimate lags or repacks
		// earlier picks. The baseline re-anchors downward when the picked total
		// drops (unticked items), so later picks still count.
		const isAcknowledgedAhead = (acknowledgedTub?.index || 0) > currentTub.index;
		const isAcknowledgedSnapshotActive = acknowledgedTub?.baseProductGrams !== null &&
			acknowledgedTub?.baseProductGrams !== undefined &&
			acknowledgedTub.index >= currentTub.index;
		const acknowledgedBaseGrams = isAcknowledgedSnapshotActive
			? Math.min(acknowledgedTub.baseProductGrams, pickedBreakdown.totalProductGrams)
			: 0;
		const acknowledgedDeltaGrams = isAcknowledgedSnapshotActive
			? pickedBreakdown.totalProductGrams - acknowledgedBaseGrams
			: 0;
		const displayTub = isAcknowledgedSnapshotActive
			? {
				index: acknowledgedTub.index,
				productGrams: acknowledgedDeltaGrams,
				totalGrams: acknowledgedDeltaGrams + (config.tubTareWeightGrams || 0),
			}
			: currentTub;
		const displaySuggestedTubProductGrams = isAcknowledgedSnapshotActive
			? requiredBreakdown?.tubes?.[displayTub.index - 1]?.productGrams || fallbackSuggestedProductGrams
			: suggestedTubProductGrams;

		return {
			currentTubIndex: currentTub.index,
			nextTubIndex: currentTub.index + 1,
			displayTubIndex: displayTub.index,
			displayTubProductKg: formatKgFromGrams(displayTub.productGrams),
			displaySuggestedTubProductKg: formatKgFromGrams(displaySuggestedTubProductGrams),
			displayTubTotalKg: formatKgFromGrams(displayTub.totalGrams),
			showTubPromptStyle: needsNextTubPrompt && !isAcknowledgedAhead,
			maxTubTotalKg: formatKgFromGrams(config.maxTubTotalWeightGrams),
			pickedTotalProductKg,
			requiredTotalProductKg,
			requiredTubCount: requiredBreakdown.tubeCount,
			missingWeightLineCount: tubWeightSummary?.totals?.missingWeightLineCount || 0,
			excludedPickedQuantity: tubWeightSummary?.totals?.excludedPickedQuantity || 0,
			needsNextTubPrompt,
		};
	}, [acknowledgedTub, tubWeightSummary]);

	useEffect(() => {
		const currentTubIndex = tubWeightDisplay?.currentTubIndex;
		const nextTubIndex = tubWeightDisplay?.nextTubIndex;
		if (!currentTubIndex) {
			return;
		}

		if (highestPromptedTubIndexRef.current === null) {
			highestPromptedTubIndexRef.current = currentTubIndex;
			highestAcknowledgedTubIndexRef.current = currentTubIndex;
			setAcknowledgedTub({ index: currentTubIndex, baseProductGrams: null });
		}

		const highestCalculatedTubIndex = tubWeightDisplay.needsNextTubPrompt && nextTubIndex
			? nextTubIndex
			: currentTubIndex;
		if (pendingTubSwitchIndex !== null || highestCalculatedTubIndex <= highestPromptedTubIndexRef.current) {
			return;
		}

		const nextPromptedTubIndex = highestPromptedTubIndexRef.current + 1;
		highestPromptedTubIndexRef.current = nextPromptedTubIndex;
		setPendingTubSwitchIndex(nextPromptedTubIndex);
	}, [
		pendingTubSwitchIndex,
		tubWeightDisplay?.currentTubIndex,
		tubWeightDisplay?.needsNextTubPrompt,
		tubWeightDisplay?.nextTubIndex,
	]);

	// Persist a lowered baseline when picked weight drops below it (unticked
	// items), so weight picked afterwards still counts toward the acknowledged tub.
	useEffect(() => {
		const totalPickedGrams = tubWeightSummary?.breakdown?.picked?.totalProductGrams;
		setAcknowledgedTub(previous => (
			previous?.baseProductGrams !== null && previous?.baseProductGrams !== undefined &&
				Number.isFinite(totalPickedGrams) && totalPickedGrams < previous.baseProductGrams
				? { ...previous, baseProductGrams: totalPickedGrams }
				: previous
		));
	}, [tubWeightSummary]);

	useEffect(() => {
		if (pendingTubSwitchIndex === null) {
			return undefined;
		}

		let isActive = true;
		Vibration.vibrate(TUB_SWITCH_VIBRATION_PATTERN, false);

		const announceTubSwitch = async () => {
			if (await Speech.isSpeakingAsync()) {
				await Speech.stop();
			}
			if (isActive) {
				Speech.speak(`Change to tub ${pendingTubSwitchIndex}`);
			}
		};
		announceTubSwitch();

		return () => {
			isActive = false;
		};
	}, [pendingTubSwitchIndex]);

	const acknowledgeTubSwitch = useEventCallback(async () => {
		if (pendingTubSwitchIndex === null) {
			return;
		}

		// This acknowledgement is workflow-only: keep it on the device so it cannot
		// alter pick quantities, tub calculations, labels or the shared pick list.
		highestAcknowledgedTubIndexRef.current = Math.max(
			highestAcknowledgedTubIndexRef.current || 0,
			pendingTubSwitchIndex,
		);
		setAcknowledgedTub({
			index: highestAcknowledgedTubIndexRef.current,
			baseProductGrams: tubWeightSummary?.breakdown?.picked?.totalProductGrams || 0,
		});
		Vibration.cancel();

		try {
			if (await Speech.isSpeakingAsync()) {
				await Speech.stop();
			}
		}
		finally {
			setPendingTubSwitchIndex(null);
		}
	});

	return {
		tubWeightDisplay,
		pendingTubSwitchIndex,
		highestAcknowledgedTubIndexRef,
		acknowledgeTubSwitch,
	};
};

export default usePickListTubWeight;
